import * as Scrivito from "scrivito";
import React, { useCallback, useMemo } from "react";
import { Button, Icon } from "components";
import { ConfirmationModal } from "components/_Modals/ConfirmationModal";
import { useTranslate } from "hooks";
import NiceModal from "@ebay/nice-modal-react";
import {
  useIsFormDirty,
  useIsMultipleQuestionnaireListStep,
  useIsFetching,
  useWizardActions,
  useWizardQuestionsValidation,
  useWizardStep,
} from "objs/_ConfiguratorObjs/Wizard/stores/wizardStore";

export const WizardSingleQuestionnaireControls = Scrivito.connect(
  ({
    handleClickContinue,
    isConfirmFetching,
    isFieldsConstraintViolated,
    handleConfirmSingleQuestionnaire,
    handleResetForm,
    isResetEnabled,
  }) => {
    const translate = useTranslate();
    const { goToPreviousStep } = useWizardActions();
    const isMultipleQuestionnaireListStep =
      useIsMultipleQuestionnaireListStep();
    const step = useWizardStep();
    const isSomethingFetching = useIsFetching();
    const isFormDirty = useIsFormDirty();
    const wizardQuestionsValidation = useWizardQuestionsValidation();

    const isConfirmDisabled = useMemo(
      () =>
        !isFormDirty ||
        isSomethingFetching ||
        Object.values(wizardQuestionsValidation).some((obj) => obj === false),
      [isFormDirty, isSomethingFetching, wizardQuestionsValidation],
    );

    const isBackDisabled = useMemo(
      () =>
        step === 0 ||
        isSomethingFetching ||
        (isFormDirty && isMultipleQuestionnaireListStep),
      [isFormDirty, isMultipleQuestionnaireListStep, isSomethingFetching, step],
    );

    const isContinueDisabled = useMemo(
      () =>
        isSomethingFetching ||
        isFieldsConstraintViolated ||
        (isFormDirty && isMultipleQuestionnaireListStep),
      [
        isSomethingFetching,
        isFieldsConstraintViolated,
        isFormDirty,
        isMultipleQuestionnaireListStep,
      ],
    );

    const handleClickBack = useCallback(() => {
      goToPreviousStep();
    }, [goToPreviousStep]);

    const handleGoToTopology = useCallback(() => {
      if (step === 3) {
        NiceModal.show(ConfirmationModal, {
          title: translate("WIZARD_SYSTEM_TOPOLOGY_CREATE"),
          iconName: "circle-info-regular",
          type: "info",
          alertText: translate("WIZARD_SYSTEM_ALERT_TEXT"),
          submitBtnText: translate("CONTINUE"),
          cancelBtnText: translate("CANCEL"),
          onConfirm: () => handleClickContinue(),
          onCancel: () => {
            NiceModal.hide(ConfirmationModal);
          },
        });
        return;
      }
      handleClickContinue();
    }, [step, handleClickContinue, translate]);

    return (
      <div className="d-flex justify-content-between gap-2 mb-4 py-2  border-top border-bottom border-gray-300">
        <Button
          data-testid="WizardSingleQuestionnaireControls_previous-btn"
          size="sm"
          color="secondary"
          outline
          disabled={isBackDisabled}
          className="border border-gray-300"
          onClick={handleClickBack}
        >
          <Icon
            prefix="trox"
            iconName="chevron-left-regular"
            className="icon-16 me-2"
          />
          {translate("PREVIOUS")}
        </Button>
        {isMultipleQuestionnaireListStep && step !== 0 && (
          <div className="d-flex gap-3">
            <Button
              data-testid="WizardSingleQuestionnaireControls_reset-to-default-btn"
              color="secondary"
              outline
              disabled={isResetEnabled}
              onClick={handleResetForm}
            >
              {translate("WIZARD_RESET_TO_DEFAULT")}
            </Button>
            <Button
              data-testid="WizardSingleQuestionnaireControls_confirm-zone-btn"
              color="secondary"
              disabled={isConfirmDisabled}
              isLoading={isConfirmFetching}
              onClick={handleConfirmSingleQuestionnaire}
            >
              {translate("WIZARD_CONFIRM_ZONE")}
            </Button>
          </div>
        )}
        <Button
          data-testid="WizardSingleQuestionnaireControls_continue-btn"
          color="secondary"
          outline
          size="sm"
          type="submit"
          className="border border-gray-300"
          disabled={isContinueDisabled}
          onClick={handleGoToTopology}
        >
          {translate("CONTINUE")}
          <Icon
            prefix="trox"
            iconName="chevron-right-regular"
            className="icon-16 ms-2"
          />
        </Button>
      </div>
    );
  },
);
